import { ALL_SERVICES } from '@shared/brand';
import type { AccountQueryResponse } from '@shared/api';
import {
  OUTREACH_TONES,
  OUTREACH_TONE_LABELS,
  type OutreachDraftDto,
  type OutreachListResponse,
  type OutreachTone,
} from '@shared/outreach';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail, PenLine, Send } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Button } from '../../components/ui/button';
import { Card, CardBody, CardHeader } from '../../components/ui/card';
import { Alert, Badge, EmptyState, Spinner } from '../../components/ui/feedback';
import { Input, Select } from '../../components/ui/form';
import { api } from '../../lib/api';
import { cn } from '../../lib/cn';
import { queryKeys } from '../../lib/query';
import { errorMessage } from '../../lib/session';
import { DraftEditor } from './draft-editor';

/** Businesses that can actually be written to — an email needs an address. */
function useContactableAccounts(search: string) {
  return useQuery({
    queryKey: queryKeys.accounts({ outreach: true, search }),
    queryFn: () =>
      api.post<AccountQueryResponse>('/api/accounts/query', {
        ...(search.trim() ? { search: search.trim() } : {}),
        filter: { conjunction: 'and', conditions: [{ field: 'hasEmail', op: 'is_true' }] },
        status: 'active',
        sort: [{ field: 'completeness', dir: 'desc' }],
        limit: 25,
      }),
  });
}

/**
 * Outreach: write a tailored email to a business this research found.
 *
 * The page is deliberately one-at-a-time. There is no "email all 400 results" button, because
 * the value here is that each message references something true about that specific business —
 * and because a bulk blast under your own Gmail account is how a domain gets a spam reputation.
 */
export function OutreachPage() {
  const [params, setParams] = useSearchParams();
  const client = useQueryClient();
  const preselected = params.get('account') ?? '';

  const [accountId, setAccountId] = useState(preselected);
  const [search, setSearch] = useState('');
  const [tone, setTone] = useState<OutreachTone>('professional');
  const [angle, setAngle] = useState('');
  const [services, setServices] = useState<string[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);

  const accounts = useContactableAccounts(search);
  const drafts = useQuery({
    queryKey: queryKeys.outreach({ status: 'all' }),
    queryFn: () => api.get<OutreachListResponse>('/api/outreach?status=all&limit=50'),
  });

  const draft = useMutation({
    mutationFn: () =>
      api.post<OutreachDraftDto>('/api/outreach/draft', {
        accountId,
        tone,
        ...(angle.trim() ? { angle: angle.trim() } : {}),
        services,
      }),
    onSuccess: async (created) => {
      setOpenId(created.id);
      setAngle('');
      await client.invalidateQueries({ queryKey: ['outreach'] });
    },
  });

  // Memoised so the lookup below does not see a new array identity on every render.
  const items = useMemo(() => drafts.data?.items ?? [], [drafts.data]);
  const open = useMemo(() => items.find((item) => item.id === openId) ?? null, [items, openId]);
  const chosen = accounts.data?.items.find((item) => item.id === accountId);

  const toggleService = (service: string): void => {
    setServices((current) =>
      current.includes(service) ? current.filter((entry) => entry !== service) : [...current, service].slice(0, 3),
    );
  };

  return (
    <div className="mx-auto max-w-[1400px] px-5 py-5">
      <header className="mb-4">
        <h1 className="text-lg font-semibold tracking-tight text-foreground">Outreach</h1>
        <p className="mt-0.5 text-[13px] text-muted-foreground">
          Write a tailored email to a business you found — grounded in what the research actually collected, reviewed by
          you, then sent from your own Gmail account.
        </p>
      </header>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,380px)_minmax(0,1fr)]">
        <div className="space-y-4">
          <Card>
            <CardHeader title="Write a new email" description="Pick a business with a contact address." />
            <CardBody className="space-y-3">
              {draft.error ? <Alert tone="error">{errorMessage(draft.error)}</Alert> : null}

              <label className="block">
                <span className="mb-1 block text-[13px] font-medium text-foreground">Find a business</span>
                <Input value={search} placeholder="Search by name, city or domain" onChange={(event) => setSearch(event.target.value)} />
              </label>

              <label className="block">
                <span className="mb-1 block text-[13px] font-medium text-foreground">Business</span>
                <Select value={accountId} onChange={(event) => setAccountId(event.target.value)}>
                  <option value="">Choose a business…</option>
                  {accounts.data?.items.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                      {item.city ? ` · ${item.city}` : ''}
                    </option>
                  ))}
                </Select>
                {accounts.isPending ? (
                  <span className="mt-1 flex items-center gap-1.5 text-[12px] text-muted-foreground">
                    <Spinner className="h-3 w-3" /> Loading businesses with an email…
                  </span>
                ) : (accounts.data?.items.length ?? 0) === 0 ? (
                  <span className="mt-1 block text-[12px] text-muted-foreground">
                    No businesses with a contact email yet. Run website enrichment, or import a list that has addresses.
                  </span>
                ) : null}
              </label>

              {chosen ? (
                <p className="text-[12px] text-muted-foreground">
                  Writing to <span className="font-medium text-foreground">{chosen.email}</span>
                </p>
              ) : null}

              <label className="block">
                <span className="mb-1 block text-[13px] font-medium text-foreground">Tone</span>
                <Select value={tone} onChange={(event) => setTone(event.target.value as OutreachTone)}>
                  {OUTREACH_TONES.map((option) => (
                    <option key={option} value={option}>
                      {OUTREACH_TONE_LABELS[option]}
                    </option>
                  ))}
                </Select>
              </label>

              <label className="block">
                <span className="mb-1 block text-[13px] font-medium text-foreground">Angle (optional)</span>
                <Input
                  value={angle}
                  placeholder="e.g. lead with the fact they have no website"
                  onChange={(event) => setAngle(event.target.value)}
                />
              </label>

              <div>
                <span className="mb-1 block text-[13px] font-medium text-foreground">
                  Services to offer <span className="font-normal text-muted-foreground">(up to 3 — or leave empty to choose from the facts)</span>
                </span>
                <div className="flex flex-wrap gap-1">
                  {ALL_SERVICES.map((service) => (
                    <button
                      key={service}
                      type="button"
                      className={cn(
                        'rounded-full border px-2.5 py-0.5 text-[12px] transition-colors duration-150',
                        services.includes(service)
                          ? 'border-accent bg-accent-subtle text-accent'
                          : 'border-border text-muted-foreground hover:border-accent hover:text-accent',
                      )}
                      onClick={() => toggleService(service)}
                    >
                      {service}
                    </button>
                  ))}
                </div>
              </div>

              <Button
                icon={<PenLine className="h-3.5 w-3.5" />}
                loading={draft.isPending}
                disabled={!accountId || draft.isPending}
                onClick={() => draft.mutate()}
              >
                Write the draft
              </Button>
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Emails" description={`${String(drafts.data?.total ?? 0)} drafted or sent`} />
            {drafts.isPending ? (
              <CardBody className="flex items-center gap-2 text-sm text-muted-foreground">
                <Spinner /> Loading…
              </CardBody>
            ) : items.length === 0 ? (
              <EmptyState icon={<Mail className="h-6 w-6" />} title="Nothing written yet" description="Draft your first email on the left." />
            ) : (
              <ul className="divide-y divide-border">
                {items.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      className={cn(
                        'flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left transition-colors duration-150 hover:bg-muted',
                        openId === item.id && 'bg-accent-subtle',
                      )}
                      onClick={() => setOpenId(item.id)}
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-[13px] font-medium text-foreground">
                          {item.accountName ?? item.toEmail}
                        </span>
                        <span className="block truncate text-[12px] text-muted-foreground">{item.subject}</span>
                      </span>
                      <Badge tone={item.status === 'sent' ? 'success' : item.status === 'failed' ? 'danger' : 'neutral'}>
                        {item.status}
                      </Badge>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        <div>
          {open ? (
            <DraftEditor
              key={open.id}
              draft={open}
              onDeleted={() => {
                setOpenId(null);
                if (preselected) setParams({});
              }}
            />
          ) : (
            <Card>
              <EmptyState
                icon={<Send className="h-6 w-6" />}
                title="No email open"
                description="Write a draft, or pick one from the list to review, edit and send it."
              />
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
